import * as THREE from 'three';

interface FramingControls {
  setTarget?: (x: number, y: number, z: number) => void;
  target?: THREE.Vector3;
  update?: () => void;
  saveState?: () => void;
  updateMatrixState?: () => void;
  _gizmos?: THREE.Object3D;
}

export interface FocusOptions {
  radius?: number;
  padding?: number;
  frustumOffsetX?: number;
}

export const focusCameraOnPoint = (
  controls: FramingControls | null,
  camera: THREE.Camera,
  point: THREE.Vector3,
  options?: FocusOptions,
): void => {
  if (!controls) return;

  if (controls.setTarget) {
    controls.setTarget(point.x, point.y, point.z);
  } else if (controls.target) {
    controls.target.set(point.x, point.y, point.z);
  }

  const radius = options?.radius;
  const isOrtho = (camera as THREE.OrthographicCamera).isOrthographicCamera;
  if (typeof radius === 'number' && radius > 0 && isOrtho) {
    const orthoCamera = camera as THREE.OrthographicCamera;
    const verticalSize = Math.abs(orthoCamera.top - orthoCamera.bottom);
    const horizontalSize = Math.abs(orthoCamera.right - orthoCamera.left);
    const minViewSize = Math.min(verticalSize, horizontalSize);
    const padding = options?.padding ?? 1.5;
    if (minViewSize > 0) {
      orthoCamera.zoom = minViewSize / (2 * radius * padding);
      orthoCamera.updateProjectionMatrix();
    }
  }

  const frustumOffsetX = options?.frustumOffsetX;
  if (typeof frustumOffsetX === 'number' && frustumOffsetX !== 0 && isOrtho) {
    const orthoCamera = camera as THREE.OrthographicCamera;
    const width = orthoCamera.right - orthoCamera.left;
    const centre = (orthoCamera.left + orthoCamera.right) / 2;
    orthoCamera.left = centre - width / 2 + frustumOffsetX;
    orthoCamera.right = centre + width / 2 + frustumOffsetX;
    orthoCamera.updateProjectionMatrix();
  }

  /* eslint-disable no-underscore-dangle */
  controls._gizmos?.updateMatrix();
  camera.updateMatrix();
  camera.updateMatrixWorld(true);
  controls.update?.();
  controls.updateMatrixState?.();
  controls.saveState?.();
};
